import {
    type Asset,
    type AssetGroup,
    type DynamicAnimationConfiguration,
    type TextAssetDynamicAnimation,
    VideoData,
} from '@lumen5/luminary';

import { getTemplateBundle } from 'js/src/data/templates';
import { isAssetTimingOverridden, setAssetTimingOverride } from 'js/src/data/user-overrides';
import { greaterThanWithinEpsilon } from 'js/src/luminary/comparison-utils';
import { withDataLuminarySync } from 'js/src/luminary/data-luminary';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    assetHasEntryAnimation,
    getAllAssetAnimations,
    getRootAssetGroup,
} from 'js/src/luminary/luminary-selectors';

export type TimingSpecification = {
    introTime: number; // absolute time in milliseconds from start of scene (video / scenegroup time, NOT transcript time)
    exitTime: number; // absolute time in milliseconds from start of scene (video / scenegroup time, NOT transcript time)
    assetId: string;
    sceneId: string;
};
type TimingSpecificationProgress = TimingSpecification & {
    complete: boolean;
};

export const setAssetIntroExitTimesOperation = createLuminaryOperation(
    (
        { luminary, call },
        {
            timingSpecifications,
            operatingOnMainLuminary,
            systemTriggered,
            parentActionGroup,
        }: {
            timingSpecifications: TimingSpecification[];
            operatingOnMainLuminary: boolean;
            systemTriggered: boolean;
            parentActionGroup: string;
        },
    ) => {
        // enforce that we only get one TimingSpecification per asset!
        const seenAssetKeys = new Set<string>();
        for (const specification of timingSpecifications) {
            const key = `${specification.sceneId}:${specification.assetId}`;
            if (seenAssetKeys.has(key)) {
                throw new Error(
                    `setAssetIntroExitTimesOperation received multiple TimingSpecifications for asset ${specification.assetId} in scene ${specification.sceneId}. Merge them into a single spec before calling.`,
                );
            }
            seenAssetKeys.add(key);
        }

        const timingSpecificationsProgress: TimingSpecificationProgress[] =
            timingSpecifications.map((specification) => {
                return {
                    ...specification,
                    complete: false,
                };
            });

        luminary.batchRun((safeLuminary) => {
            timingSpecificationsProgress.forEach((specification) => {
                if (specification.complete) {
                    return;
                }
                const scene = safeLuminary.getSceneById(specification.sceneId);
                if (!scene) {
                    return;
                }

                const asset = scene.getAssetById(specification.assetId);
                if (!asset) {
                    throw new Error('Asset not found in scene');
                }

                const rootAssetGroup = getRootAssetGroup(asset);
                if (rootAssetGroup) {
                    call(
                        _manageAssetGroupAndChildAssets,
                        rootAssetGroup,
                        {
                            timingSpecificationsProgress,
                            operatingOnMainLuminary,
                            systemTriggered,
                            parentActionGroup,
                        },
                        { luminary: safeLuminary },
                    );
                } else {
                    call(
                        _manageAsset,
                        asset,
                        {
                            timingSpecificationsProgress,
                            operatingOnMainLuminary,
                            systemTriggered,
                            parentActionGroup,
                        },
                        { luminary: safeLuminary },
                    );
                }
            });
        });
    },
);

const _manageAssetGroupAndChildAssets = createLuminaryOperation(
    (
        { luminary, dispatch, call },
        assetGroup: AssetGroup,
        {
            timingSpecificationsProgress,
            operatingOnMainLuminary,
            systemTriggered,
            parentActionGroup,
        }: {
            timingSpecificationsProgress: TimingSpecificationProgress[];
            operatingOnMainLuminary: boolean;
            systemTriggered: boolean;
            parentActionGroup: string;
        },
    ) => {
        assetGroup.introAnimationMode = VideoData.AT_THE_SAME_TIME_ANIMATION_MODE;
        assetGroup.outroAnimationMode = VideoData.AT_THE_SAME_TIME_ANIMATION_MODE;
        assetGroup.childIntroAnimationOffset = 0;
        assetGroup.childOutroAnimationOffset = 0;

        if (
            assetGroup.colorGraphics.length > 0 ||
            assetGroup.alphaGraphics.length > 0 ||
            assetGroup.hasObjectAnimation
        ) {
            // if the AssetGroup has color or alpha graphics, we want those to
            // enter when the first child asset enters
            // and exit when the last child asset exits
            const children = assetGroup.getAssets().filter((child) => {
                if (
                    VideoData.isColorAsset(child) ||
                    VideoData.isCircleAsset(child) ||
                    VideoData.isGradientAsset(child) ||
                    VideoData.isRectangleAsset(child) ||
                    VideoData.isCompositingAsset(child)
                ) {
                    // these assets do not have a maxHeight property, so we need to handle them separately to avoid ts errors
                    // I do not think they should ever actually be children of an asset group, but just in case...
                    return true;
                }
                // children with no content (like text assets with empty text "") get their maxHeight set to 0
                // by the asset group. For these children, we do not need to consider their entry/exit times.
                return child.maxHeight > 0;
            });
            const smallestIntroAnimationOffset =
                children
                    .map((child) => {
                        const timingSpecificationProgress = _findTimingSpecificationProgress(
                            timingSpecificationsProgress,
                            child.id,
                        );
                        if (timingSpecificationProgress) {
                            return timingSpecificationProgress.introTime;
                        }
                        return child.appliedIntroAnimationOffset;
                    })
                    .sort((a, b) => a - b)[0] ?? 0;
            const smallestOutroAnimationOffset =
                children
                    .map((child) => {
                        const timingSpecificationProgress = _findTimingSpecificationProgress(
                            timingSpecificationsProgress,
                            child.id,
                        );
                        if (timingSpecificationProgress && child.luminaryParent) {
                            return (
                                child.luminaryParent.duration - timingSpecificationProgress.exitTime
                            );
                        }
                        return child.appliedOutroAnimationOffset;
                    })
                    .sort((a, b) => a - b)[0] ?? 0;
            _minimizeAnimationDelays(assetGroup);
            assetGroup.introAnimationOffset = smallestIntroAnimationOffset;
            assetGroup.outroAnimationOffset = smallestOutroAnimationOffset;
            assetGroup.animateOutBehaviour = call(_calculateAnimateOutBehaviour, assetGroup);
        }
        assetGroup.getAssets({ deep: false }).forEach((asset) => {
            if (VideoData.isAssetGroup(asset)) {
                call(_manageAssetGroupAndChildAssets, asset, {
                    timingSpecificationsProgress,
                    operatingOnMainLuminary,
                    systemTriggered,
                    parentActionGroup,
                });
                return;
            }
            call(_manageAsset, asset, {
                timingSpecificationsProgress,
                operatingOnMainLuminary,
                systemTriggered,
                parentActionGroup,
            });
        });
    },
);

const _findTimingSpecificationProgress = (
    timingSpecificationsProgress: TimingSpecificationProgress[],
    assetId: string,
) => {
    return timingSpecificationsProgress.find((specification) => specification.assetId === assetId);
};

const _manageAsset = createLuminaryOperation(
    (
        { dispatch, getState, call },
        asset: Asset,
        {
            timingSpecificationsProgress,
            operatingOnMainLuminary,
            systemTriggered,
            parentActionGroup,
        }: {
            timingSpecificationsProgress: TimingSpecificationProgress[];
            operatingOnMainLuminary: boolean;
            systemTriggered: boolean;
            parentActionGroup: string;
        },
    ) => {
        const hasEntryAnimation = assetHasEntryAnimation(asset);
        if (!hasEntryAnimation) {
            // The asset has no enabled entry animation (it never had one, or its animations were
            // turned off). Create/enable a pop-in animation so the custom timing has something to
            // apply to.
            ensureAssetTimingPopInAnimations(asset);
        }

        const timingSpecificationProgress = _findTimingSpecificationProgress(
            timingSpecificationsProgress,
            asset.id,
        );
        if (timingSpecificationProgress && asset.luminaryParent) {
            const outroAnimationOffset =
                asset.luminaryParent.duration - timingSpecificationProgress.exitTime;
            _minimizeAnimationDelays(asset);
            asset.introAnimationOffset = timingSpecificationProgress.introTime;
            asset.outroAnimationOffset = outroAnimationOffset;
            asset.animateOutBehaviour = call(_calculateAnimateOutBehaviour, asset);
        } else {
            _minimizeAnimationDelays(asset);
            asset.introAnimationOffset = asset.appliedIntroAnimationOffset;
            asset.outroAnimationOffset = asset.appliedOutroAnimationOffset;
        }
        if (
            operatingOnMainLuminary &&
            !isAssetTimingOverridden(getState(), asset.slide!.id, asset.id)
        ) {
            dispatch(
                setAssetTimingOverride({
                    sceneId: asset.slide!.id,
                    assetId: asset.id,
                    isOverridden: true,
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
    },
);
const _minimizeAnimationDelays = (asset: Asset) => {
    // each animation configuration can have a delay. If all the animations
    // have a delay built into them, it is effectively increasing the introAnimationOffset
    // for the entire asset. This throws off the calculations for the timing:
    // asset.appliedIntroAnimationOffset won't equal asset.introAnimationOffset!
    // To get around this, we make it so that the delay of each animation is shortened
    // so that at least one animation has a delay of 0.
    const allAnimations = getAllAssetAnimations(asset);
    let minAnimationDelay = Infinity;
    for (let i = 0; i < allAnimations.length; i++) {
        if (allAnimations[i].delay < minAnimationDelay) {
            minAnimationDelay = allAnimations[i].delay;
        }
    }
    if (minAnimationDelay < Infinity) {
        allAnimations.forEach((animation) => {
            animation.delay -= minAnimationDelay;
        });
    }
};
type AlphaAnimationConfiguration = DynamicAnimationConfiguration | TextAssetDynamicAnimation;

const _hasEnabledEntryAlphaAnimation = (
    animationConfigurations: AlphaAnimationConfiguration[],
): boolean =>
    animationConfigurations.some(
        (animation) =>
            animation.enabled &&
            animation.property === 'alpha' &&
            typeof animation.startValue === 'number' &&
            typeof animation.endValue === 'number' &&
            animation.startValue === 0 &&
            animation.endValue > 0,
    );

/**
 * Ensures a single animation collection (e.g. an asset's objectAnimation, or a text style's
 * animationConfigurations) has an enabled alpha 0 -> 1 "pop-in" animation. Collections that
 * already animate in are left untouched; an existing pop-in animation is re-used/re-enabled
 * rather than duplicated; and the disabled scene-design animations are left alone.
 */
const _ensurePopInAlphaAnimation = (
    animationConfigurations: AlphaAnimationConfiguration[],
    addAnimation: () => AlphaAnimationConfiguration,
) => {
    if (_hasEnabledEntryAlphaAnimation(animationConfigurations)) {
        return;
    }
    const existingPopInAnimation = animationConfigurations.find(
        (animation) =>
            animation.property === 'alpha' &&
            animation.creationSource === VideoData.AnimationCreationSource.PopIn,
    );
    const animation = existingPopInAnimation ?? addAnimation();
    animation.property = 'alpha';
    animation.duration = 10;
    animation.startValue = 0;
    animation.endValue = 1;
    animation.enabled = true;
    animation.creationSource = VideoData.AnimationCreationSource.PopIn;
};

/**
 * Ensures the asset has an enabled pop-in (alpha 0 -> 1) animation so that custom intro/exit
 * times have something to apply to. Used when an asset has no enabled scene-design entry
 * animation — either it never had one, or its animations were turned off via the animation
 * toggle. Collections that already animate in are left untouched.
 */
export const ensureAssetTimingPopInAnimations = (asset: Asset) => {
    if (VideoData.isMediaAsset(asset) || VideoData.isIconAsset(asset)) {
        _ensurePopInAlphaAnimation(asset.objectAnimation, () => asset.addObjectAnimation());
    }
    if (VideoData.isTextAsset(asset) || VideoData.isFancyCaptionAsset(asset)) {
        _ensurePopInAlphaAnimation(asset.normalStyle.animationConfigurations, () =>
            asset.normalStyle.addAnimation(),
        );
    }
    if (VideoData.isTextAsset(asset)) {
        _ensurePopInAlphaAnimation(asset.highlightStyle.animationConfigurations, () =>
            asset.highlightStyle.addAnimation(),
        );
        for (const graphic of asset.highlightStyle.graphics) {
            _ensurePopInAlphaAnimation(graphic.animationConfigurations, () =>
                graphic.addAnimation(),
            );
        }
    }
};

const _calculateAnimateOutBehaviour = createLuminaryOperation(
    ({ luminary, getState }, asset: Asset) => {
        const noForcedOutroAnimation = VideoData.isSubscene(asset.luminaryParent)
            ? VideoData.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING
            : VideoData.ANIMATE_OUT_BEHAVIOUR_NEVER;

        if (greaterThanWithinEpsilon(asset.outroAnimationOffset, 0)) {
            // if the asset is going to exit before the end of the container, we need to animate it out
            return VideoData.ANIMATE_OUT_BEHAVIOUR_ALWAYS;
        }

        // if the outro animation offset is 0, we need to still decide whether or not to animate out the asset
        // use the scene design to determine the right answer, defaulting to never.
        const templateBundle = getTemplateBundle(getState(), luminary.template.id);
        if (!templateBundle) {
            return noForcedOutroAnimation;
        }

        return withDataLuminarySync(
            (dataLuminary) => {
                const { scene: dataScene } = dataLuminary.addScene({
                    sceneDesignId: asset.slide!.sceneDesign,
                });
                const dataAsset = dataScene.getAssetByName(asset.name);
                if (dataAsset) {
                    return dataAsset.animateOutBehaviour;
                }
                return noForcedOutroAnimation;
            },
            {
                templateBundle,
            },
        );
    },
);
